import { ProfileAiEvaluation } from '../ai-service';
import { CustomField, Generic } from '../dictionary-service';
import { Profile } from '../notification-service';
import { User } from '../recruiter-service';
import { Shortlist } from '../shortlist-service';
import { Employee } from './profile-employee';
import {
  CandidateFile,
  LinkContact,
  MainContacts,
  ProfileComment,
  ProfileReminder,
  ShortlistCounts,
} from './profile-entities';

export type XRayRange = '2-months' | '12-months' | 'all-time';

export interface InternalCount {
  candidates: number;
  profiles: number;
}

export interface ExternalCount {
  github: number;
  rabotaua: number;
}

export interface XRayLink {
  range: XRayRange;
  source: string;
  type: string;
  url: string;
  withSynonyms: boolean;
}

export interface Profile {
  aiEvaluations: ProfileAiEvaluation[];
  applies: Shortlist.Item | null;
  availableContacts: string[];
  bidCounts: ShortlistCounts;
  birthday: string | null;
  bookedBy: User.Base | null;
  bookedDate: string | null;
  candidatesData: Profile[];
  cityId: number | null;
  color: string;
  comments: ProfileComment[];
  commentsCount: number;
  countryId: number | null;
  createdAt: string;
  cvs: CandidateFile[];
  cvsLength: number;
  cvSummary: string | null;
  currency: string | null;
  customFields: CustomField.Item[];
  duplicatesCount: number;
  englishLevel: number | null;
  firstName: string;
  id: number;
  image: string | null;
  insideInformation: string | null;
  isEmployee: boolean;
  lastComment: ProfileComment | null;
  lastName: string | null;
  location: string | null;
  mainSkill: number | null;
  name: string;
  professionalLevel: number | null;
  otherFiles: CandidateFile[];
  otherFilesLength: number;
  rate: number | null;
  rateCard: number | null;
  salaryComment: string | null;
  simpleDuplicates: SimpleProfile[];
  skills: number[];
  softSkills: number[];
  source: Generic.Item | null;
  specialityId: number | null;
  summary: string | null;
  tags: Generic.Item[];
  viewedByRecruiter: Date | null;
  voices: CandidateFile[];
  voicesLength: number;
}

export enum ProfileType {
  CANDIDATE,
  EXTERNAL,
}

export interface FullProfile extends Profile {
  addedBy: User.Base;
  city: Generic.Item | null;
  country: Generic.Item | null;
  employeeData: Employee | null;
  files: CandidateFile[];
  mainContacts: MainContacts;
  matchedContacts: Record<string, string[]>;
  middleName: string | null;
  notifications: Profile.Entry[];
  notificationCounts: number;
  processingCount: number;
  processingList: Shortlist.Item[];
  reminders: ProfileReminder | null;
  speciality: Generic.Item | null;
  socialLinks: LinkContact[];
  updatedAt: string | null;
}

export type ProfileContacts = Pick<
  FullProfile,
  'id' | 'name' | 'mainContacts' | 'socialLinks'
>;

export type SimpleProfile = Pick<
  Profile,
  'id' | 'name' | 'createdAt' | 'firstName' | 'lastName'
> & {
  profileId: number;
};

export interface ProfileForExport extends Pick<
  Profile,
  | 'currency'
  | 'insideInformation'
  | 'name'
  | 'location'
  | 'rate'
  | 'rateCard'
  | 'summary'
> {
  applyStatusName: string | null;
  applyStatusChangeDate: string | null;
  emails: string;
  phones: string;
  lastWorkingPlace: string | null;
  lastWorkingPosition: string | null;
  socialUrls: string;
  sourceName: string;
  telegrams: string;
}

export interface MatchProfilePayload {
  id: number;
  publicProfileId: string;
  isMatch: boolean;
}

export interface MatchProfileResponse extends MatchProfilePayload {
  createdAt: string;
}
